import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  duplicateCommercialDocument,
  newCommercialDocument,
  type CommercialDocument,
  type CommercialDocumentFiling,
} from "../../shared/commercial-documents";
import { OpaqueIdSchema } from "../../shared/schemas";
import { Button } from "../components/ui/button";
import { ContentLoading } from "../components/ContentLoading";
import { FieldError } from "../components/ui/field";
import { CommercialDocumentEditor } from "./CommercialDocumentEditor";
import { getCommercialDocument } from "./commercial-documents-api";
import type { CommercialDocumentRoute } from "./customer-route";
import { replaceOperationsLocation, type OperationsNavigate } from "./navigation";

type PageProps = {
  route: CommercialDocumentRoute;
  customerName: string;
  canRead: boolean;
  canWrite: boolean;
  navigate: OperationsNavigate;
};

export function CommercialDocumentPage(props: PageProps) {
  if (!props.canRead || (props.route.kind && !props.canWrite))
    return <FieldError>You do not have permission to open this document.</FieldError>;
  return <DocumentPage {...props} />;
}

/** `?from=<id>` on a new-document route starts the draft from that saved document's content. */
function duplicateSourceId() {
  const parsed = OpaqueIdSchema.safeParse(new URLSearchParams(window.location.search).get("from"));
  return parsed.success ? parsed.data : null;
}

/**
 * This page stays mounted when Duplicate moves from a saved document to a new draft, so each
 * new-draft route gets its own identifier until that draft's first save.
 */
function useDraftIdentity(route: CommercialDocumentRoute) {
  const [draftIds] = useState(() => new Map<string, string>());
  const sourceId = route.kind ? duplicateSourceId() : null;
  const draftKey = `${route.kind ?? ""}:${sourceId ?? ""}`;
  let draftId = draftIds.get(draftKey);
  if (!draftId) {
    draftId = `commercial_${crypto.randomUUID().replaceAll("-", "")}`;
    draftIds.set(draftKey, draftId);
  }
  return {
    id: route.documentId ?? draftId,
    sourceId,
    release: () => draftIds.delete(draftKey),
  };
}
function useCommercialDocument(slug: string, id: string, enabled: boolean, key: unknown[] = []) {
  return useQuery({
    queryKey: ["ops", "commercial-document", slug, id, ...key],
    queryFn: () => getCommercialDocument(slug, id),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: 0,
  });
}

function DocumentPage({ route, customerName, canWrite, navigate }: PageProps) {
  const client = useQueryClient();
  const { id, sourceId, release } = useDraftIdentity(route);
  const source = useCommercialDocument(route.slug, sourceId ?? "", sourceId !== null, [
    "duplicate",
  ]);
  const query = useCommercialDocument(route.slug, id, !route.kind);
  const close = () => {
    navigate(`/customers/${route.slug}/documents`);
  };
  const editorProps = {
    slug: route.slug,
    id,
    canWrite,
    onClose: close,
    onSaved: (record: CommercialDocument) => {
      client.setQueryData(["ops", "commercial-document", route.slug, id], { document: record });
      void client.invalidateQueries({ queryKey: ["ops", "commercial-documents", route.slug] });
      if (!route.kind) return;
      release();
      replaceOperationsLocation(`/customers/${route.slug}/documents/${record.id}`);
    },
    onFiled: (record: CommercialDocument, filing: CommercialDocumentFiling) => {
      client.setQueryData(["ops", "commercial-document", route.slug, id], {
        document: record,
        filing,
      });
      void client.invalidateQueries({ queryKey: ["ops", "commercial-documents", route.slug] });
      void client.invalidateQueries({ queryKey: ["ops", "customer-library", route.slug] });
    },
    onDuplicate: (record: CommercialDocument) => {
      navigate(`/customers/${route.slug}/documents/new/${record.kind}?from=${record.id}`);
    },
  };
  if (route.kind) {
    if (sourceId && source.isPending) return <ContentLoading label="Copying document" />;
    const copied = source.data?.document.document;
    return (
      <CommercialDocumentEditor
        key={`${route.slug}:${id}`}
        {...editorProps}
        initial={
          copied?.kind === route.kind
            ? duplicateCommercialDocument(copied)
            : newCommercialDocument(route.kind, customerName)
        }
      />
    );
  }
  if (query.isPending) return <ContentLoading label="Opening document" />;
  if (query.error)
    return (
      <div className="space-y-3">
        <FieldError>{query.error.message}</FieldError>
        <Button
          onClick={() => {
            void query.refetch();
          }}
        >
          Retry
        </Button>
        <Button variant="ghost" onClick={close}>
          Back to documents
        </Button>
      </div>
    );
  return (
    <CommercialDocumentEditor
      key={`${route.slug}:${id}`}
      {...editorProps}
      initial={query.data.document.document}
      current={query.data.document}
      filing={query.data.filing ?? null}
    />
  );
}
